import type { Address } from 'viem'

/** Robinhood Chain. Keep in sync with `src/lib/appkit.ts`. */
export const CURVE_CHAIN_ID = 4663

export const BLOCKSCOUT_URL = (import.meta.env.VITE_BLOCKSCOUT_URL || 'https://robinhoodchain.blockscout.com').replace(/\/$/, '')
export const RH_SCAN_URL = (import.meta.env.VITE_RH_SCAN_URL || 'https://rh-scan.com').replace(/\/$/, '')

/** Live addresses from deployments/latest.json. Env overrides when set. */
export const LIVE_CURVE_FACTORY = '0x09f1B640d53C9C4C1807BF5Aa306745128Ee4375' as Address
/** New curve factory. All NEW createLaunch calls go here; existing listings keep reading from LIVE_CURVE_FACTORY. */
export const LIVE_CURVE_FACTORY_V2 = '0x6747bcc869c39d964A36E4796bead3FA3b377b73' as Address
export const LIVE_PRESALE_FACTORY = '0x2d49B4B881CD27b917e4321839fc4B28a95d12a0' as Address
export const LIVE_PENGUIN_HOOK = '0x19B4517dED421d738051545e6C5d461ef13460cc' as Address
export const LIVE_V4_GRADUATE = '0x4aE34aDDDDA5f74E6bBf2AA4E7fa1709C4A02f55' as Address
export const LIVE_POOL_MANAGER = '0x8366a39CC670B4001A1121B8F6A443A643e40951' as Address
export const LIVE_POSITION_MANAGER = '0x58daec3116aae6D93017bAAea7749052E8a04fA7' as Address
export const LIVE_WETH = '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73' as Address

const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/

export function isEthAddress(value: string | undefined | null): value is Address {
  return typeof value === 'string' && ADDRESS_RE.test(value)
}

export function isNonZeroAddress(value: string | undefined | null): value is Address {
  return isEthAddress(value) && value.toLowerCase() !== '0x0000000000000000000000000000000000000000'
}

function envAddress(raw: string | undefined, fallback: Address): Address {
  const trimmed = (raw || '').trim()
  return isEthAddress(trimmed) ? trimmed : fallback
}

export function getCurveFactoryAddress(): Address {
  return envAddress(import.meta.env.VITE_CURVE_FACTORY_ADDRESS, LIVE_CURVE_FACTORY)
}

/** New curve factory for all NEW launches. Existing listings on LIVE_CURVE_FACTORY are left as-is. */
export function getCurveFactoryAddressV2(): Address {
  return envAddress(import.meta.env.VITE_CURVE_FACTORY_ADDRESS_V2, LIVE_CURVE_FACTORY_V2)
}

/** Both live curve factories, newest first. Used so listing never drops existing V1 launches. */
export function listCurveFactoryAddresses(): Address[] {
  const unique: Address[] = []
  for (const factory of [getCurveFactoryAddressV2(), getCurveFactoryAddress()]) {
    if (!isEthAddress(factory))
      continue
    if (unique.some(item => item.toLowerCase() === factory.toLowerCase()))
      continue
    unique.push(factory)
  }
  return unique
}

export function getPresaleFactoryAddress(): Address {
  return envAddress(import.meta.env.VITE_PRESALE_FACTORY_ADDRESS, LIVE_PRESALE_FACTORY)
}

export function getPenguinHookAddress(): Address {
  return envAddress(import.meta.env.VITE_PENGUIN_HOOK_ADDRESS, LIVE_PENGUIN_HOOK)
}

export function getV4GraduateAddress(): Address {
  return envAddress(import.meta.env.VITE_V4_GRADUATE_ADDRESS, LIVE_V4_GRADUATE)
}

export function isOnChainCurveEnabled() {
  return Boolean(getCurveFactoryAddress())
}

export function isOnChainPresaleEnabled() {
  return Boolean(getPresaleFactoryAddress())
}

export function sameAddress(a?: string | null, b?: string | null) {
  if (!a || !b)
    return false
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

export function shortenAddress(address: string) {
  if (!isEthAddress(address))
    return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

export function explorerTokenUrl(tokenAddress: string) {
  return `${BLOCKSCOUT_URL}/token/${tokenAddress}`
}

export function explorerAddressUrl(address: string) {
  return `${BLOCKSCOUT_URL}/address/${address}`
}

export function explorerWalletUrl(address: string) {
  return `${RH_SCAN_URL}/address/${address}`
}

export function walletLast4(address: string) {
  if (!isEthAddress(address))
    return address.slice(-4) || address
  return address.slice(-4)
}

export function explorerTxUrl(hash: string) {
  return `${BLOCKSCOUT_URL}/tx/${hash}`
}
